/**
 * Car preference sections for the transports MiniModal (excludes car usage mode).
 */

import React, { useCallback, useMemo } from "react";
import { carPrefsSlice, usePersistedSlice } from "../../../../../store/persistence";
import {
    type CarParkAndRidePreference,
    type CarPtAccessMode,
} from "../../../../../types/GlobalContext";
import ImageOptionSelector, { type ImageSelectorOption } from "../../../../MiniModal/ImageOptionSelector";
import MiniModalSection from "../../../../MiniModal/MiniModalSection";
import MiniModalSelect, { type MiniModalSelectOption } from "../../../../MiniModal/MiniModalSelect";

/** Supported public-transport access option ids exposed in the selector. */
type CarPtAccessOptionId = CarPtAccessMode;

/** Personal car ownership/carpooling options shown in the first section. */
const CAR_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "personal_car",
        "label":    "Voiture personnelle",
        "imageSrc": "/assets/icon/car_prefs/car_usage/PersonalCar.webp",
        "imgCover": true,
    },
    {
        "id":       "carpooling",
        "label":    "Covoiturage",
        "imageSrc": "/assets/icon/car_prefs/car_usage/Carpooling.webp",
        "imgCover": true,
    },
];

/** Public-transport access options displayed in the car MiniModal section. */
const CAR_PT_ACCESS_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "train",
        "label":    "Accès à la gare",
        "imageSrc": "/assets/icon/car_prefs/pt_access/CarTrain.webp",
        "imgCover": true,
    },
    {
        "id":       "parking",
        "label":    "Liaisons P+R",
        "imageSrc": "/assets/icon/car_prefs/pt_access/CarParking.webp",
        "imgCover": true,
    },
];

/** Park-and-ride preference options for {@link MiniModalSelect}. */
const PARK_AND_RIDE_SELECT_OPTIONS: readonly MiniModalSelectOption[] = [
    {
        "id":       "MAX_TRANSPORT",
        "label":    "Maximiser les transports publics",
        "sublabel": "Priorise le trajet en TC après le parking",
    },
    {
        "id":       "MAX_CAR",
        "label":    "Maximiser la voiture",
        "sublabel": "Priorise la portion en voiture",
    },
    {
        "id":       "BALANCED",
        "label":    "Équilibré",
        "sublabel": "Compromis voiture / transports publics",
    },
    {
        "id":       "ALL",
        "label":    "Une proposition de chaque",
        "sublabel": "Propose les trois stratégies",
    },
];

const PARK_AND_RIDE_PREFERENCE_IDS: readonly CarParkAndRidePreference[] = [
    "ALL",
    "MAX_TRANSPORT",
    "MAX_CAR",
    "BALANCED",
];

/**
 * Type guard for `CarPtAccessOptionId`.
 *
 * @param value Candidate string value.
 * @returns `true` when `value` is a supported PT access option id.
 */
const isCarPtAccessOptionId = ( value: string ): value is CarPtAccessOptionId => {
    return value === "train" || value === "parking";
};

/**
 * Type guard for persisted park-and-ride preference ids.
 *
 * @param value Candidate select value.
 * @returns `true` when `value` is a known preference.
 */
const isCarParkAndRidePreference = ( value: string ): value is CarParkAndRidePreference => {
    return ( PARK_AND_RIDE_PREFERENCE_IDS as readonly string[] ).includes( value );
};

interface CarPrefsModalContentProps {

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;
}

/**
 * Car preference sections (PT access) for the transports modal.
 *
 * @param props Component props. See `CarPrefsModalContentProps`.
 * @returns Car MiniModal sections excluding usage mode.
 */
const CarPrefsModalContent: React.FC<CarPrefsModalContentProps> = ( { isDarkUi } ) => {
    const [ carPrefs, setCarPrefs ] = usePersistedSlice( carPrefsSlice );
    const isPtAccessEnabled = carPrefs.carUsageMode === "personal_car";
    const isParkAndRideEnabled = isPtAccessEnabled && carPrefs.ptAccessModes.includes( "parking" );
    const carOptionsSelection = useMemo<readonly string[]>( () => {
        const selectedOptions: string[] = [];
        if ( carPrefs.carUsageMode === "personal_car" ) {
            selectedOptions.push( "personal_car" );
        }
        if ( carPrefs.carpoolingEnabled === true ) {
            selectedOptions.push( "carpooling" );
        }

        return selectedOptions;
    }, [ carPrefs.carUsageMode, carPrefs.carpoolingEnabled ] );

    const ptAccessSelection = useMemo<readonly string[]>( () => {
        return isPtAccessEnabled ? [ ...carPrefs.ptAccessModes ] : [];
    }, [ carPrefs.ptAccessModes, isPtAccessEnabled ] );

    const ptAccessSelectorOptions = useMemo<readonly ImageSelectorOption[]>( () => {
        return CAR_PT_ACCESS_OPTIONS.map( ( option ) => ( {
            ...option,
            "disabled":        !isPtAccessEnabled,
            "disabledTooltip": isPtAccessEnabled
                ? undefined
                : "Indisponible sans voiture personnelle.",
        } ) );
    }, [ isPtAccessEnabled ] );

    /**
     * Apply selected public-transport access modes. Ignores unknown option ids.
     *
     * @param nextValue Selected checkbox values.
     */
    const handlePtAccessChange = useCallback( ( nextValue: readonly string[] ): void => {
        if ( !isPtAccessEnabled ) {
            return;
        }

        const ptAccessModes = nextValue.filter( isCarPtAccessOptionId );

        setCarPrefs( {
            ...carPrefs,
            "ptAccessModes": ptAccessModes,
        } );
    }, [ carPrefs, isPtAccessEnabled, setCarPrefs ] );

    /**
     * Apply personal car / carpooling options.
     *
     * @param nextValue Selected checkbox values.
     */
    const handleCarOptionsChange = useCallback( ( nextValue: readonly string[] ): void => {
        const selectedOptions = new Set( nextValue );
        const isPersonalCar = selectedOptions.has( "personal_car" );
        const isCarpoolingEnabled = selectedOptions.has( "carpooling" );

        setCarPrefs( {
            ...carPrefs,
            "carUsageMode":      isPersonalCar ? "personal_car" : "no_car",
            "ptAccessModes":     isPersonalCar ? carPrefs.ptAccessModes : [],
            "carpoolingEnabled": isCarpoolingEnabled,
        } );
    }, [ carPrefs, setCarPrefs ] );

    /**
     * Apply the park-and-ride routing preference from the select.
     *
     * @param nextValue Selected preference id.
     */
    const handleParkAndRidePreferenceChange = useCallback( ( nextValue: string ): void => {
        if ( !isParkAndRideEnabled || !isCarParkAndRidePreference( nextValue ) ) {
            return;
        }

        setCarPrefs( {
            ...carPrefs,
            "parkAndRidePreference": nextValue,
        } );
    }, [ carPrefs, isParkAndRideEnabled, setCarPrefs ] );

    return (
        <>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Options"
            >
                <ImageOptionSelector
                    columns={2}
                    isDarkUi={isDarkUi}
                    onChange={handleCarOptionsChange}
                    options={CAR_OPTIONS}
                    selectionType="checkbox"
                    value={carOptionsSelection}
                />
            </MiniModalSection>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Accès en voiture aux transports"
            >
                <ImageOptionSelector
                    columns={2}
                    isDarkUi={isDarkUi}
                    onChange={handlePtAccessChange}
                    options={ptAccessSelectorOptions}
                    selectionType="checkbox"
                    value={ptAccessSelection}
                />
            </MiniModalSection>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Options P+R"
            >
                <MiniModalSelect
                    ariaLabel="Préférence de routage park-and-ride"
                    disabled={!isParkAndRideEnabled}
                    isDarkUi={isDarkUi}
                    onChange={handleParkAndRidePreferenceChange}
                    options={PARK_AND_RIDE_SELECT_OPTIONS}
                    value={carPrefs.parkAndRidePreference}
                />
            </MiniModalSection>
        </>
    );
};

export default CarPrefsModalContent;
